import { noop } from "lodash";
import React, { ReactElement } from "react";
import { isEventCard } from "../EventCard/EventCard";
import { isEventCardColumnGroup } from "./EventCardColumnGroup";
import EventCardColumnHeading, { headingLevel } from "./EventCardColumnHeading";
import EventCardColumnLoading from "./EventCardColumnLoading";
import EventCardColumnMessage from "./EventCardColumnMessage";

type EventCardColumnProps = {
  emptyMessage?: string;
  errorMessage?: string;
  heading: string;
  headingLevel?: headingLevel;
  loading?: boolean;
  onFlexibilityChange?: (eventId: string, isFlexible: boolean) => void;
  updating?: boolean;
};

export const isEventCardColumn = (
  component: React.ReactNode,
): component is ReactElement<EventCardColumnProps> => {
  if (
    !component ||
    !React.isValidElement(component) ||
    typeof component.type === "string" ||
    typeof component.type === "number" ||
    component.type.name !== EventCardColumn.name
  ) {
    return false;
  }

  return true;
};

const EventCardColumn = ({
  children,
  emptyMessage = "no meetings",
  errorMessage = "",
  heading,
  headingLevel = "h3",
  loading = false,
  onFlexibilityChange = noop,
  updating = false,
}: React.PropsWithChildren<EventCardColumnProps>) => {
  const childrenWithProps = React.Children.map(children, (child) => {
    if (isEventCard(child) || isEventCardColumnGroup(child)) {
      return React.cloneElement(child as JSX.Element, {
        updating,
        onFlexibilityChange,
        ...child.props,
      });
    }

    return child;
  });

  return (
    <div className="cw-flex cw-h-fit cw-bg-neutral cw-border cw-rounded cw-border-default cw-p-3 cw-border-solid">
      <div className="cw-space-y-2 cw-grow">
        <EventCardColumnHeading heading={heading} headingLevel={headingLevel} />
        <EventCardColumnMessage message={errorMessage} isError />
        <EventCardColumnLoading loading={loading} />
        {!loading && childrenWithProps}
        {!loading && !React.Children.count(childrenWithProps) && (
          <div className="cw-ml-3">
            <EventCardColumnMessage message={emptyMessage} />
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCardColumn;
